import { Injectable, LOCALE_ID, OnDestroy, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import {
  LayerModel, LayerTypesEnum, MapService, OgcHelper, ServiceLayerModel, WMSLayerModel, WMTSLayerModel, XyzLayerModel, Tiles3dLayerModel,
  TerrainLayerModel, PROJECTION_REQUIRED_FOR_3D, MapViewerOptionsModel,
} from '@tailormap-viewer/map';
import { ServerType, ServiceModel, ServiceProtocol } from '@tailormap-viewer/api';
import {
  combineLatest, concatMap, distinctUntilChanged, filter, first, forkJoin, map, Observable, of, Subject, switchMap, take,
  takeUntil, tap,
} from 'rxjs';
import { HttpClient, HttpParams } from '@angular/common/http';
import { DomSanitizer } from '@angular/platform-browser';
import { ArrayHelper, HtmlifyHelper, Tileset3dStyleHelper } from '@tailormap-viewer/shared';
import {
  selectMapOptions, selectOrderedVisibleBackgroundLayers, selectOrderedVisibleLayersWithServices, select3DLayers, selectIn3dView,
} from '../state/map.selectors';
import { ExtendedAppLayerModel } from '../models';
import { selectCQLFilters } from '../../state/filter-state/filter.selectors';
import { withLatestFrom } from 'rxjs/operators';
import { BookmarkService } from '../../services/bookmark/bookmark.service';
import { MapBookmarkHelper } from '../../services/application-bookmark/map-bookmark.helper';
import { ApplicationBookmarkFragments } from '../../services/application-bookmark/application-bookmark-fragments';
import { ApplicationLayerRefreshService } from './application-layer-refresh.service';
import { FeaturesFilterHelper } from '../../filter';
import { MobileLayoutService } from '../../services/viewer-layout/mobile-layout.service';

@Injectable({
   providedIn: 'root',
})
export class ApplicationMapService implements OnDestroy {
  private store$ = inject(Store);
  private mapService = inject(MapService);
  private httpClient = inject(HttpClient);
  private bookmarkService = inject(BookmarkService);
  private localeId = inject(LOCALE_ID);
  private _applicationLayerRefreshService = inject(ApplicationLayerRefreshService);
  private mobileLayoutService = inject(MobileLayoutService);
  private sanitizer = inject(DomSanitizer);

  private destroyed = new Subject();
  private capabilities: Map<string, string> = new Map();

  public init() {
    const isValidLayer = (layer: LayerModel | null): layer is LayerModel => layer !== null;
    this.store$.select(selectMapOptions)
      .pipe(
        takeUntil(this.destroyed),
        filter(mapOptions => !!mapOptions),
        distinctUntilChanged((prev, curr) => {
          if (prev === null || curr === null) {
            return false;
          }
          return prev.projection === curr.projection &&
            ArrayHelper.arrayEquals(prev.initialExtent, curr.initialExtent) &&
            ArrayHelper.arrayEquals(prev.maxExtent, curr.maxExtent);
        }),
        withLatestFrom(
          this.bookmarkService.registerFragment$<string>(ApplicationBookmarkFragments.LOCATION_BOOKMARK_DESCRIPTOR),
          this.mobileLayoutService.isMobileLayoutEnabled$,
        ),
      )
      .subscribe(([ mapOptions, locationBookmark, isMobileLayoutEnabled ]) => {
        if (mapOptions === null) {
          return;
        }
        const bookmark = MapBookmarkHelper.locationAndZoomFromFragment(locationBookmark);
        const initialOptions = bookmark ? { initialCenter: bookmark[0], initialZoom: bookmark[1] } : undefined;
        const mapViewerOptions: MapViewerOptionsModel = {
          ...mapOptions,
          controlOptions: { attributionPosition: isMobileLayoutEnabled ? 'left' : 'right' },
        };
        this.mapService.initMap(mapViewerOptions, initialOptions);
      });

    combineLatest([
      this.store$.select(selectOrderedVisibleBackgroundLayers).pipe(
        concatMap(layers => this.getLayersAndLayerManager$(layers)),
      ),
      this.store$.select(selectIn3dView),
      this.store$.select(selectMapOptions),
    ])
      .pipe(takeUntil(this.destroyed))
      .subscribe(([[ layers, layerManager ], in3DView, mapOptions ]) => {
        if (in3DView && mapOptions?.projection !== PROJECTION_REQUIRED_FOR_3D) {
          layerManager.setBackgroundLayers(layers.filter(isValidLayer).filter(layer => layer.webMercatorAvailable), PROJECTION_REQUIRED_FOR_3D);
        } else {
          layerManager.setBackgroundLayers(layers.filter(isValidLayer), mapOptions?.projection);
        }
      });

    combineLatest([
      this.selectOrderedVisibleLayersWithFilters$().pipe(
        concatMap(layers => this.getLayersAndLayerManager$(layers)),
      ),
      this.store$.select(selectIn3dView),
      this.store$.select(selectMapOptions),
    ])
      .pipe(takeUntil(this.destroyed))
      .subscribe(([[ layers, layerManager ], in3DView, mapOptions ]) => {
        if (in3DView && mapOptions?.projection !== PROJECTION_REQUIRED_FOR_3D) {
          layerManager.setLayers(layers.filter(isValidLayer).filter(
            layer => layer.webMercatorAvailable || layer.layerType === LayerTypesEnum.Vector,
          ), PROJECTION_REQUIRED_FOR_3D);
        } else {
          layerManager.setLayers(layers.filter(isValidLayer), mapOptions?.projection);
        }
      });

    this.store$.select(selectIn3dView)
      .pipe(first(in3d => in3d))
      .subscribe(() => {
        this.mapService.make3D();
      });

    this.store$.select(selectIn3dView)
      .pipe(
        first(in3d => in3d),
        switchMap(() => this.store$.select(select3DLayers)),
        takeUntil(this.destroyed),
        concatMap(layers => this.get3DLayersAndLayerManager$(layers)),
      )
      .subscribe(([ layers, layerManager ]) => {
        layerManager.addLayers(layers.filter(isValidLayer));
      });
  }

  public selectOrderedVisibleLayersWithFilters$() {
    return combineLatest([
      this.store$.select(selectOrderedVisibleLayersWithServices),
      this.store$.select(selectCQLFilters),
    ]).pipe(
      map(([ layers, filters ]) => {
        return layers.map(l => ({
          ...l,
          filter: FeaturesFilterHelper.getFilter(filters.get(l.id)),
        }));
      }),
    );
  }

  private getLayersAndLayerManager$(serviceLayers: ExtendedAppLayerModel[]) {
    const layers$ = serviceLayers
      .map(layer => this.convertAppLayerToMapLayer$(layer));
    return forkJoin([
      layers$.length > 0 ? forkJoin(layers$) : of([]),
      this.mapService.getLayerManager$().pipe(take(1)),
    ]);
  }

  private get3DLayersAndLayerManager$(serviceLayers: ExtendedAppLayerModel[]) {
    const layers$ = serviceLayers
      .map(layer => this.convertAppLayerToMapLayer$(layer));
    return forkJoin([
      layers$.length > 0 ? forkJoin(layers$) : of([]),
      this.mapService.getCesiumManager$().pipe(take(1)),
    ]);
  }

  public ngOnDestroy() {
    this.destroyed.next(null);
    this.destroyed.complete();
  }

  public convertAppLayerToMapLayer$(extendedAppLayer: ExtendedAppLayerModel): Observable<LayerModel | null> {
    if (!extendedAppLayer.service) {
      return of(null);
    }

    const service = extendedAppLayer.service;
    const defaultLayerProps: ServiceLayerModel = {
      id: `${extendedAppLayer.id}`,
      name: extendedAppLayer.temporaryLayerName || extendedAppLayer.layerName,
      url: extendedAppLayer.url || service.url,
      layerType: LayerTypesEnum.WMS,
      visible: extendedAppLayer.visible,
      minScale: extendedAppLayer.minScale,
      maxScale: extendedAppLayer.maxScale,
      // We don't want a 'tainted canvas' for features such as printing. TM requires CORS-enabled or proxied services.
      crossOrigin: 'anonymous',
      opacity: extendedAppLayer.opacity,
      attribution: typeof extendedAppLayer.attribution === 'string'
        ? HtmlifyHelper.htmlifyContents(extendedAppLayer.attribution, this.sanitizer)
        : extendedAppLayer.attribution,
      hiDpiDisabled: extendedAppLayer.hiDpiDisabled,
    };
    if (service.protocol === ServiceProtocol.WMTS) {
      return this.getCapabilitiesForWMTS$(service)
        .pipe(
          map((capabilities: string): WMTSLayerModel => ({
            ...defaultLayerProps,
            layerType: LayerTypesEnum.WMTS,
            layers: extendedAppLayer.layerName,
            capabilities: capabilities || '',
            hiDpiMode: extendedAppLayer.hiDpiMode,
            hiDpiSubstituteLayer: extendedAppLayer.hiDpiSubstituteLayer,
            webMercatorAvailable: extendedAppLayer.webMercatorAvailable,
          })),
        );
    }
    if (service.protocol === ServiceProtocol.WMS) {
      const layer: WMSLayerModel = {
        ...defaultLayerProps,
        layerType: LayerTypesEnum.WMS,
        layers: extendedAppLayer.temporaryLayerName || extendedAppLayer.layerName,
        serverType: service.serverType,
        tilingDisabled: extendedAppLayer.tilingDisabled,
        tilingGutter: extendedAppLayer.tilingGutter,
        filter: typeof extendedAppLayer.filter === 'string' ? extendedAppLayer.filter : undefined,
        language: service.serverType === ServerType.GEOSERVER ? this.localeId : undefined,
        webMercatorAvailable: extendedAppLayer.webMercatorAvailable,
        styles: extendedAppLayer.styles,
        selectedStyleName: extendedAppLayer.selectedStyleName,
      };
      return of(layer);
    }
    if (service.protocol === ServiceProtocol.XYZ) {
      const layer: XyzLayerModel = {
        ...defaultLayerProps,
        layerType: LayerTypesEnum.XYZ,
        hiDpiMode: extendedAppLayer.hiDpiMode,
        hiDpiSubstituteUrl: extendedAppLayer.hiDpiSubstituteLayer,
        minZoom: extendedAppLayer.minZoom,
        maxZoom: extendedAppLayer.maxZoom,
        tileSize: extendedAppLayer.tileSize,
        tileGridExtent: extendedAppLayer.tileGridExtent,
        webMercatorAvailable: extendedAppLayer.webMercatorAvailable,
      };
      return of(layer);
    }
    if (service.protocol === ServiceProtocol.TILES3D) {
      const layer: Tiles3dLayerModel = {
        ...defaultLayerProps,
        layerType: LayerTypesEnum.TILES3D,
        tileset3dStyle: Tileset3dStyleHelper.isTileset3dStyle(extendedAppLayer.tileset3dStyle) ? extendedAppLayer.tileset3dStyle : undefined,
      };
      return of(layer);
    }
    if (service.protocol === ServiceProtocol.QUANTIZEDMESH) {
      const layer: TerrainLayerModel = {
        ...defaultLayerProps,
        layerType: LayerTypesEnum.QUANTIZEDMESH,
      };
      return of(layer);
    }
    return of(null);
  }

  private getCapabilitiesForWMTS$(service: ServiceModel): Observable<string> {
    if (service.capabilities) {
      return of(service.capabilities);
    }
    const cachedCapabilities = this.capabilities.get(service.id);
    if (cachedCapabilities) {
      return of(cachedCapabilities);
    }
    return this.httpClient.get(OgcHelper.filterOgcUrlParameters(service.url), {
      responseType: 'text',
      params: new HttpParams().append('REQUEST', 'GetCapabilities').append('SERVICE', 'WMTS'),
    }).pipe(
      tap(capabilities => this.capabilities.set(service.id, capabilities)),
    );
  }

}
